// /var/www/afra/web-design.rampagesoft.com/my-app/src/component/ContentDetail.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { BreadCrumb } from 'primereact/breadcrumb';
import { ProgressSpinner } from 'primereact/progressspinner';
import { fetchPostImage, fetchPosts, incrementVisitors } from '../services/api';
import MetaTagContentDetail from '../metatag/MetaTagContentDetail';
import Slidebar from '../component/slidebar.js';
import '../css/ContentDetail.scss';
import 'primeicons/primeicons.css';

// Utility functions
const stripHtml = html => {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
};

const truncateText = (text, max) =>
  text.length <= max ? text : text.substring(0, max) + '...';

const createUrlFriendlyString = text =>
  encodeURIComponent(text)
    .replace(/%20/g, '-')
    .replace(/%[0-9A-F]{2}/g, match => (match === '%2F' ? '/' : match));

const formatDate = dateString => {
  const d = new Date(dateString);
  return d.toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
};

// Determine correct image URL
const getImageUrl = (imgValue, defaultImage) => {
  if (!imgValue) return defaultImage;
  // Remove leading dot and normalize URL
  const cleanImgValue = imgValue.replace(/^\./, '').trim();
  if (cleanImgValue.startsWith('http://') || cleanImgValue.startsWith('https://')) {
    return cleanImgValue;
  }
  return `https://maracar.rampagesoft.com/model/upload/img/IMG_Home/${cleanImgValue}`;
};

export default function ContentDetail() {
  const { id } = useParams();
  const [content, setContent] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [slidebarData, setSlidebarData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const heading = 'CONTENT IDEAS LAB';
  const defaultImage = 'https://afaa.website/s/ff6970.png';

  useEffect(() => {
    async function fetchContentDetail() {
      try {
        setLoading(true);
        setError(null);

        // Fetch all posts and find by ID
        const data = await fetchPosts(100);
        const post = data.results?.find(p => String(p.Home_ID) === id);
        if (!post) throw new Error(`No post found for ID: ${id}`);
        console.log('Fetched post:', post);

        // Fetch raw image field
        let rawImg = '';
        try {
          const imageData = await fetchPostImage(id);
          rawImg = imageData.results?.[0]?.ImgHome_Img || '';
          console.log('Raw image:', rawImg);
        } catch (e) {
          console.error(`Failed to fetch image for ID ${id}:`, e.message);
        }
        // Compute final URL
        const finalUrl = getImageUrl(rawImg, defaultImage);
        console.log('Final image URL:', finalUrl);

        setContent(post);
        setImageUrl(finalUrl);

        // Increment visitors
        try {
          await incrementVisitors(id);
        } catch (e) {
          console.error('Failed to increment visitors:', e.message);
        }

        // Fetch Slidebar data
        const resp = await fetchPosts(10);
        const items = await Promise.all(
          (resp.results || []).filter(p => p.Home_ID && p.Home_Sub).map(async p => {
            let raw = '';
            try {
              const imgData = await fetchPostImage(p.Home_ID);
              raw = imgData.results?.[0]?.ImgHome_Img || '';
            } catch {}
            const url = getImageUrl(raw, defaultImage);
            const title = truncateText(stripHtml(p.Home_Sub || ''), 50);
            const desc = truncateText(stripHtml(p.Home_Short || ''), 200);
            const link = `/content/${p.Home_ID}/${createUrlFriendlyString(stripHtml(p.Home_Sub || ''))}`;
            return { id: p.Home_ID, img: url, title, description: desc, link, namelink: 'Read More', style: {} };
          })
        );
        setSlidebarData(items);
      } catch (e) {
        console.error('Fetch content error:', e);
        setError(e.message || 'Failed to load content');
      } finally {
        setLoading(false);
      }
    }
    fetchContentDetail();
  }, [id]);

  if (loading) return <div className="content-detail__loading"><ProgressSpinner /></div>;
  if (error) return <div className="content-detail__error">{error}</div>;
  if (!content) return <div className="content-detail__empty">No content found</div>;

  const breadcrumbItems = [
    { label: 'Home', url: '/', icon: 'pi pi-home' },
    { label: 'Content', url: '/content', icon: 'pi pi-folder' },
    { label: truncateText(stripHtml(content.Home_Sub || ''), 50), disabled: true }
  ];

  const currentUrl = `${window.location.origin}/content/${id}/${createUrlFriendlyString(stripHtml(content.Home_Sub || ''))}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": content.Home_Sub || 'Untitled',
    "datePublished": content.Home_Date || new Date().toISOString(),
    "author": { "@type": "Organization", "name": "AFRA APACHE" },
    "image": imageUrl,
    "url": currentUrl,
    "description": stripHtml(content.Home_Short || '')
  };

  return (
    <section>
      <MetaTagContentDetail
        title={content.Home_Sub || 'Untitled'}
        description={stripHtml(content.Home_Short || '').substring(0, 160) || 'No description'}
        url={currentUrl}
        date={content.Home_Date || new Date().toISOString()}
        jsonLd={jsonLd}
      />
      <div className="container">
        <BreadCrumb model={breadcrumbItems} className="content-detail__breadcrumb" />
        <article className="content-detail">
          <header className="content-detail__header">
            <h1 className="content-detail__title">{content.Home_Sub || 'Untitled'}</h1>
            <div className="content-detail__meta">
              <span className="content-detail__meta-item">
                <i className="pi pi-calendar" /> {formatDate(content.Home_Date || new Date())}
              </span>
              <span className="content-detail__meta-item">
                <i className="pi pi-eye" /> {content.Home_Visitors || 0}
              </span>
            </div>
          </header>
          {imageUrl && (
            <figure className="content-detail__image-container">
              <img
                src={imageUrl}
                alt={content.Home_Sub || 'Content Image'}
                className="content-detail__image"
                onError={() => console.error(`Failed to load image: ${imageUrl}`)}
              />
            </figure>
          )}
          <section className="content-detail__details">
            <div dangerouslySetInnerHTML={{ __html: content.Home_Detail || 'No content available' }} />
          </section>
        </article>
      </div>
      <Slidebar data={slidebarData} heading={heading} theme="primary-home" />
    </section>
  );
}